import {
  Component,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  ViewChild,
} from '@angular/core';
import { WorkforceAppComponent } from 'src/app/workforce/app.wf.component';
import { ActivatedRoute, Router } from '@angular/router';
import { LedgersService } from 'ldt-ledger-service-api-v2';
import { Ledger } from './ledger.types';
import { ImportsService as ImportsServiceV2 } from 'ldt-ledger-service-api-v2';
import { BehaviorSubject, merge, Observable, Subject, timer } from 'rxjs';
import { delay, map, retryWhen, share, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { AuthService } from '../auth/service/auth.service';
import { NotificationService } from '../shared/notification-service/notification.service';
import { LedgerContactsV2Component } from './ledger-contacts-v2/ledger-contacts-v2/ledger-contacts-v2.component';
import { LedgerImportComponent } from './ledger-import/ledger-import.component';

@Component({
  selector: 'app-ledger',
  templateUrl: './ledger.component.html',
  styleUrls: ['./ledger.component.scss'],
  standalone: false,
})
export class LedgerComponent implements OnInit, OnDestroy {
  // The Workforce shell provides no content padding; pad ourselves when
  // rendered inside it (the GLD shell pads its content container).
  @HostBinding('class') get hostPadding(): string {
    return this.wfParent
      ? 'tw-box-border tw-block tw-p-4 md:tw-p-8 tw-max-w-screen-3xl tw-mx-auto tw-overflow-x-hidden'
      : '';
  }

  // Get the imports component so we can trigger a refresh of imports grid after an upload
  @ViewChild(LedgerImportComponent) importComponent: LedgerImportComponent;
  @ViewChild(LedgerContactsV2Component) contactsComponent: LedgerContactsV2Component;
  currentTabIndex = 0;
  ledger: Ledger;
  orgId: string;
  useV2Import: boolean = false;

  @Input() placeholder: string = 'Click to add a description';

  // TODO make this async - may not be necessary since the component is reloaded for every ledger
  canEdit: boolean = false;

  importsInProgress$: BehaviorSubject<any> = new BehaviorSubject<any>({
    importing: false,
    processing: false,
  });
  private stopPolling = new Subject<void>();
  private lastPoll = false;
  private manualTrigger = new Subject<void>();
  atContactLimit: boolean = false;

  showSpace: boolean = false;

  closeSpace() {
    this.showSpace = false;
  }

  showUploader() {
    this.showSpace = true;
  }

  constructor(
    @Optional() public wfParent: WorkforceAppComponent | null,
    private ledgerService: LedgersService,
    private importService: ImportsServiceV2,
    private route: ActivatedRoute,
    private auth: AuthService,
    private router: Router,
    private notify: NotificationService
  ) {
    const thisOrg = this.auth.getSelectedOrg();
    if (!thisOrg) {
      // In theory this should never happen cause if there are no orgs the OrgsGuard will redirect to a diff page
      this.router.navigateByUrl('/main');
    } else {
      let settings: any = thisOrg.settings;
      let limit = settings?.ledger?.contactLimit;
      // TODO limit
    }
    this.canEdit = this.auth.userHasRole('editor');
  }

  isAdmin: Observable<boolean>;
  hasImports: boolean = false;

  async ngOnInit() {
    this.ledger = this.route.snapshot.data.userdata;
    this.orgId = this.auth.getSelectedOrgIdValue;
    this.isAdmin = this.auth.$isAdmin;

    this.isAdmin.subscribe({
      next: (isAdmin) => {
        if (isAdmin) {
          this.useV2Import = true;
        }
      },
    });

    // Check every 30s to see if there are active imports. If so, show a notification and
    //  update the contacts and imports lists while imports are running. Also do one last
    //  update when it flips from true -> false, and have a manual trigger when a user does an import
    //  Might want to change this to return the imports[] instead of a boolean if we want to do more with them
    merge(timer(1, 60000), this.manualTrigger.asObservable())
      .pipe(
        switchMap(() => this.importService.getLedgerImports(this.orgId, this.ledger.id)),
        tap((res) => {
          this.hasImports = (res.imports || []).length > 0;
        }),
        map((res) => {
          return {
            importing: (res.imports || []).some((i) => i.status === 'importing'),
            processing: (res.imports || []).some((i) => i.status === 'processing'),
          };
        }),
        retryWhen((errors) => errors.pipe(delay(30000), take(10))),
        share(),
        takeUntil(this.stopPolling),
        tap((x) => {
          this.importsInProgress$.next(x);
          if (x.importing || x.processing || this.lastPoll) {
            this.triggerContactsRefresh();
            this.triggerImportRefresh();
            this.updateLedger();
          }
          this.lastPoll = x.importing || x.processing;
        })
      )
      .subscribe();

    const state = history.state;
    if (state.showAddContacts !== undefined) {
      this.showSpace = state.showAddContacts;
    }
    if (state.atContactLimit !== undefined) {
      this.atContactLimit = state.atContactLimit;
    }
  }

  ngAfterViewInit() {}

  triggerImportRefresh(): void {
    if (this.importComponent) {
      this.importComponent.refreshData();
    }
  }

  triggerContactsRefresh(): void {
    if (this.contactsComponent) {
      this.contactsComponent.refreshData(true);
    }
  }

  updateLedgerSettings(orgSettings: Ledger) {
    this.ledger = orgSettings;
  }

  onLedgerNameChange(newName: any) {
    let updateBody = { name: newName.textContent, description: this.ledger.description };
    this.ledgerService.updateOrgLedger(this.orgId, this.ledger.id, updateBody).subscribe(
      (res) => {
        if (res) {
          this.updateLedger();
        }
      },
      () => {
        this.notify.error('Oops. There was an error during your request. Please try again later.');
      }
    );
  }

  onLedgerDescriptionChange(newDesc: any) {
    let updateBody = { description: newDesc.textContent, name: this.ledger.name };
    this.ledgerService.updateOrgLedger(this.orgId, this.ledger.id, updateBody).subscribe(
      (res) => {
        if (res) {
          this.updateLedger();
        }
      },
      () => {
        this.notify.error('Oops. There was an error during your request. Please try again later.');
      }
    );
  }

  ignoreCR(e: any) {
    if (e.keyCode === 13) {
      return false;
    }
    return true;
  }

  updateLedger() {
    this.ledgerService.getLedgerById(this.orgId, this.ledger.id).subscribe(
      (res) => {
        this.ledger = res;
      },
      () => {
        // Do nothing yet
      }
    );
  }

  uploadComplete() {
    timer(1000).subscribe(() => {
      this.manualTrigger.next();
    });
  }

  ngOnDestroy() {
    // Stop the imports polling
    this.stopPolling.next();
  }
}
